import { collection, query, where, getDocs, doc, deleteDoc, runTransaction, setDoc, updateDoc, onSnapshot, Unsubscribe, DocumentSnapshot } from 'firebase/firestore';
import { db, functions, httpsCallable } from '@/firebase';
import { SignUpData, Traveller, PaginationOptions, PaginatedResult } from '@/types';
import { 
  createPaginatedQuery, 
  executePaginatedQuery, 
  listenToPaginatedQuery 
} from '@/lib/paginationUtils';

const getSignUpsCollectionRef = (tripId: string) => collection(db, 'trips', tripId, 'signUps');

export const listenToPendingSignUps = (tripId: string, callback: (signUps: SignUpData[]) => void): Unsubscribe => {
    const collectionRef = getSignUpsCollectionRef(tripId);
    const q = query(collectionRef, where("status", "==", "pending"));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const signups = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SignUpData));
        callback(signups);
    });

    return unsubscribe;
};

// New paginated methods
export const getPendingSignUpsPaginated = async (
    tripId: string,
    paginationOptions: PaginationOptions = {}
): Promise<PaginatedResult<SignUpData>> => {
    const collectionRef = getSignUpsCollectionRef(tripId);
    const baseQuery = query(collectionRef, where("status", "==", "pending"));
    
    const paginatedQuery = createPaginatedQuery(baseQuery, paginationOptions, 'submittedAt');
    
    return executePaginatedQuery(paginatedQuery, (doc: DocumentSnapshot) => ({
        id: doc.id,
        ...doc.data()
    } as SignUpData));
};

export const listenToPendingSignUpsPaginated = (
    tripId: string,
    callback: (result: PaginatedResult<SignUpData>) => void,
    paginationOptions: PaginationOptions = {}
): Unsubscribe => {
    const collectionRef = getSignUpsCollectionRef(tripId);
    const baseQuery = query(collectionRef, where("status", "==", "pending"));
    
    const paginatedQuery = createPaginatedQuery(baseQuery, paginationOptions, 'submittedAt');
    
    return listenToPaginatedQuery(paginatedQuery, callback, (doc: DocumentSnapshot) => ({
        id: doc.id,
        ...doc.data()
    } as SignUpData));
};

export const getAllSignUpsPaginated = async (
    tripId: string,
    paginationOptions: PaginationOptions = {}
): Promise<PaginatedResult<SignUpData>> => {
    const collectionRef = getSignUpsCollectionRef(tripId);
    
    const paginatedQuery = createPaginatedQuery(collectionRef, paginationOptions, 'submittedAt');
    
    return executePaginatedQuery(paginatedQuery, (doc: DocumentSnapshot) => ({
        id: doc.id,
        ...doc.data()
    } as SignUpData));
};

export const listenToAllSignUpsPaginated = (
    tripId: string,
    callback: (result: PaginatedResult<SignUpData>) => void,
    paginationOptions: PaginationOptions = {}
): Unsubscribe => {
    const collectionRef = getSignUpsCollectionRef(tripId);
    
    const paginatedQuery = createPaginatedQuery(collectionRef, paginationOptions, 'submittedAt');
    
    return listenToPaginatedQuery(paginatedQuery, callback, (doc: DocumentSnapshot) => ({
        id: doc.id,
        ...doc.data()
    } as SignUpData));
};

export const deleteSignUp = async (tripId: string, signUpId: string): Promise<void> => {
    const signUpRef = doc(db, 'trips', tripId, 'signUps', signUpId);
    await deleteDoc(signUpRef);
};

export const convertSignUpToTraveller = async (tripId: string, signUp: SignUpData, newTravellerData: Omit<Traveller, 'id'>): Promise<Traveller> => {
    return await runTransaction(db, async (transaction) => {
        const signUpRef = doc(db, 'trips', tripId, "signUps", signUp.id!);
        const newTravellerRef = doc(collection(db, 'trips', tripId, 'travellers'));
        
        // Use the new traveller's generated ID in the document
        const finalTraveller = { ...newTravellerData, id: newTravellerRef.id };

        transaction.set(newTravellerRef, finalTraveller);
        transaction.update(signUpRef, { status: 'confirmed' });
        return finalTraveller;
    });
};

export const submitSignUp = async (tripId: string, signUpData: SignUpData): Promise<unknown> => {
    const onTripSignUp = httpsCallable(functions, 'onTripSignUp');
    const result = await onTripSignUp({ ...signUpData, tripId });
    return result.data;
};
